import { useContext, useMemo } from 'react';
import MantineTable from '../../../../../../common/table/mantine';
import { IconCheck } from '@tabler/icons-react';
import { Button, Tooltip } from '@mantine/core';
import { Status } from '../../../../styles';
import moment from 'moment';
import { CostingRequestsContext } from '../../../../../../contexts/CostingRequest';
import AssignedUsersList from '../../../../../../components/assignedUsersList';

const BuyingTable = ({ handleRefresh, handleSave, data, loading, setLogsModal, fetchLogs }) => {
  const { info, isCrRoleAuthorized } = useContext(CostingRequestsContext);
  const { producing_plant_data } = info;

  const columns = useMemo(
    () => [
      {
        accessorKey: 'action',
        header: 'Action',
        Cell: ({ row }) => {
          const plant = row.original?.producing_plant;
          const status = row.original?.status;
          const hasRefreshAccess = isCrRoleAuthorized(producing_plant_data[0].producing_country);

          const isDisabled = status !== 'In Progress' || !hasRefreshAccess;

          return (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}
            >
              {
                <Tooltip label='Action' position='top' withArrow>
                  <Button
                    variant='subtle'
                    color='dark'
                    size='sm'
                    style={{ width: '100%' }}
                    onClick={() => handleRefresh(row.original)}
                    disabled={isDisabled}
                  >
                    <IconCheck color='green' variant='filled' size={16} />
                  </Button>
                </Tooltip>
              }
            </div>
          );
        },
      },
      {
        accessorKey: 'sku_number',
        header: 'SKU No',
      },
      {
        accessorKey: 'sku_description',
        header: 'SKU Desc',
      },
      {
        accessorKey: 'sales_org',
        header: 'Purchase Org',
      },
      {
        accessorKey: 'distribution_channel',
        header: 'Distribution Channel',
      },
      {
        accessorKey: 'key_combination',
        header: 'Key Combination',
      },
      {
        accessorKey: 'country',
        header: 'Country',
      },
      {
        accessorKey: 'condition_type',
        header: 'Condn Type',
      },
      {
        accessorKey: 'cost_per_hl',
        header: 'Cost/HL',
      },
      {
        accessorKey: 'cost_per_100hl',
        header: 'Cost/100 HL',
      },
      {
        accessorKey: 'stock_price',
        header: 'Stock Price',
      },
      {
        accessorKey: 'currency_key',
        header: 'Curr',
      },
      {
        accessorKey: 'per',
        header: 'Unit',
      },
      {
        accessorKey: 'unit_of_measure',
        header: 'UoM',
      },
      {
        accessorKey: 'valid_from',
        header: 'Valid From',
      },
      {
        accessorKey: 'valid_to',
        header: 'Valid To',
      },
      {
        id: 'status',
        header: 'Status',
        Cell: ({ row }) => <Status status={row.original.status} />,
      },
      {
        id: 'logs',
        header: 'Logs',
        Cell: ({ row }) => {
          const rowId = row.original?.id;
          return (
            <Button
              color='dark'
              onClick={async () => {
                try {
                  const logs = await await fetchLogs(rowId);
                  setLogsModal({
                    open: true,
                    logs: logs,
                    type: 'TP ERP Setup Check',
                  });
                } catch (error) {
                  console.error('Error fetching logs:', error);
                }
              }}
            >
              View Logs
            </Button>
          );
        },
      },
      {
        accessorKey: 'assignedTo',
        header: 'FP&A GCC SME',
        Cell: ({
          row: {
            original: { assignedTo },
          },
        }) => <AssignedUsersList users={assignedTo} />,
      },
      {
        accessorKey: 'completed_on',
        header: 'Completed On',
      },
    ],
    [loading, producing_plant_data],
  );

  return (
    <>
      <MantineTable
        columns={columns}
        initialData={data}
        unique='id'
        enableRowSelection={false}
        loading={loading}
        showSelectedToggle={false}
        hideSelectColumn={true}
        showResetAll={false}
        enableExport={true}
        csvFilename={`tp_erp_setup_check_${moment().format('YYYY-MM-DD')}`}
        pageSize={10}
      />
    </>
  );
};

export default BuyingTable;
